import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Chrome } from "lucide-react";
import { Eyebrow, EASE } from "@/components/motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const inputCls = "w-full bg-[#171A22] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all text-sm";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(form.email, form.password);
      toast.success("Welcome back.");
      navigate(data.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-20 px-6" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="w-full max-w-md bg-[#101218] border border-white/10 rounded-2xl p-8 md:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.5)] relative overflow-hidden"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
        <Eyebrow className="mb-2">Clear Path Repair</Eyebrow>
        <h1 className="font-serif text-3xl text-white tracking-tight mb-8">Team sign in.</h1>
        <form onSubmit={submit} className="space-y-4">
          <input required type="email" placeholder="Email" value={form.email} data-testid="login-email-input"
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          <input required type="password" placeholder="Password" value={form.password}
            data-testid="login-password-input"
            onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
          {error && <p data-testid="login-error" className="text-red-400 text-sm">{error}</p>}
          <button type="submit" disabled={loading} data-testid="login-submit-button"
            className="w-full bg-gold text-obsidian hover:bg-gold-light hover:scale-[1.01] active:scale-[0.99] font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 rounded-xl transition-all duration-200 shadow-[0_0_20px_rgba(93,123,255,0.35)] disabled:opacity-50">
            {loading ? "One moment…" : "Sign In"}
          </button>
        </form>
        <div className="flex items-center gap-3 mt-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
        <button
          type="button"
          data-testid="google-signin-button"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/login";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="mt-4 w-full flex items-center justify-center gap-3 bg-white/5 border border-white/15 text-white hover:bg-white/10 hover:border-white/30 font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 rounded-xl transition-all duration-200"
        >
          <Chrome className="w-4 h-4 text-gold" /> Continue with Google
        </button>
      </motion.div>
    </div>
  );
}
